import cookie from "js-cookie";
import _flatten from "lodash/flatten";
import _intersection from "lodash/intersection";
import _isEmpty from "lodash/isEmpty";
import _uniq from "lodash/uniq";
import moment from "moment-timezone";
import qs from "query-string";
import sanitizeHtml from "sanitize-html";

import { KEY_CODE, MOBILE_BREAK, REASON_FOR_LEAVING } from "lib/constants";

/**
 * True if the value is null or undefined
 */
export function isNullOrUndefined(value) {
    return value === null || value === undefined;
}

/**
 * Returns a new object with the keys renamed
 * for display purposes.
 * */
export const keyToTitle = (str) =>
    str
        .split("_")
        .map((p) => p.substr(0, 1).toUpperCase() + p.substr(1).toLowerCase())
        .join(" ");

/**
 * True if the value is null or undefined or an empty string
 */
export function isNullUndefinedOrEmpty(value) {
    return value === null || value === undefined || value === "";
}

/**
 * Removes item from the array.
 *
 * THIS FUNCTION MODIFIES THE ARRAY IN-PLACE AND IS NOT FOR IMMUTABLE ARRAYS.
 */
export function arrayRemove(array, item) {
    const index = array.indexOf(item);
    if (index === -1) {
        return;
    }

    array.splice(index, 1);
}

/**
 * Moves an item around in an array
 *
 * THIS FUNCTION MODIFIES THE ARRAY IN-PLACE AND IS NOT FOR IMMUTABLE ARRAYS.
 */
export function arrayMove(array, from, to) {
    if (from === -1) {
        return;
    }

    array.splice(to, 0, array.splice(from, 1)[0]);
}

/**
 * Adds or removes an item from an array
 */
export function arrayToggle(array, item) {
    const newArray = array.slice();

    const index = array.indexOf(item);
    if (index === -1) {
        newArray.push(item);
    } else {
        newArray.splice(index, 1);
    }

    return newArray;
}

export function getRate(count, subset) {
    return !count ? 0 : Math.floor(((count - subset) / count) * 100);
}

/**
 * Formats a phone number for display
 * @param phone
 * @param dashes defaults false, returns phone in dashes format if true
 * @returns {string}
 */
export function formatPhone(phone) {
    if (!phone) {
        return "";
    }

    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
}

/**
 * Adds some asterisks for a phone number to mask it
 * @param {string} phone
 * @returns {string}
 */
export const maskPhone = (phone) => (phone ? `******${phone.slice(6)}` : "");

/**
 * Shows the first 2 chars and the TLD for an email address. Kinda
 * assumes you have a valid email address to start with.
 *
 * @param {string} email
 * @returns {string}
 */
export function maskEmail(email) {
    if (!email) {
        return "";
    }

    const [name, domain] = email.split("@");
    const maskedName = "*".repeat(name.length - 2);

    const dParts = domain.split(".");
    const tld = dParts.pop();
    const maskedDomain = "*".repeat(dParts.join(".").length);

    return `${name.slice(0, 2)}${maskedName}@${maskedDomain}.${tld}`;
}

/**
 * Returns a link to the schedule page
 *
 * @param calendarID
 * @returns {string}
 */
export function scheduleURL(calendarID) {
    return `/portal/calendar/${calendarID}/`;
}

/*
 * https://css-tricks.com/snippets/javascript/get-url-variables/
 * Because JUSTIN is a monster
 */
export function getQueryParam(variable) {
    const query = window.location.search.substring(1);
    const vars = query.split("&");
    for (let i = 0; i < vars.length; i += 1) {
        const pair = vars[i].split("=");
        if (pair[0] === variable) {
            return pair[1];
        }
    }

    return null;
}

export function getObjectValues(obj) {
    return Object.keys(obj).map((k) => obj[k]);
}

/**
 * Very dumb check to ensure an input date falls in the last year or beyond.
 * Basically just a loose check to make sure we aren't getting weird 1917 dates
 * or something. AAdding a check that it's not more than 100 years in the future
 * in case someone adds an extra digit accidentally as it let those through prior.
 *
 * @param {string} inputDate
 * @returns {boolean}
 */
export function looseRecentDateCheck(inputDate) {
    const date = moment.utc(inputDate);
    return (
        date.isValid() &&
        date.isAfter(moment.utc().subtract(1, "year")) &&
        date.isBefore(moment.utc().add(100, "year"))
    );
}

/**
 * Checks if a given value is found inside a bitmask value
 */
export function bitmaskContains(mask, value) {
    return (mask & value) === value;
}

/**
 * Finds all values inside a given bitmask
 */
export function getValuesFromBitmask(mask, possibleValues) {
    return Object.keys(possibleValues)
        .map((k) => (bitmaskContains(mask, possibleValues[k]) ? possibleValues[k] : null))
        .filter((v) => v);
}

/**
 * Calculates a user's match score
 */
export function calculateMatchScore(count) {
    return Math.max(5, Math.min(100, Math.round(22 * Math.log(count + count / 2))));
}

/**
 * Return CSS class to denote some amount of progress
 */
export function getProgressClass(percent) {
    if (percent === 0) {
        return "progress-zero";
    }

    if (percent < 40) {
        return "progress-low";
    }

    if (percent < 80) {
        return "progress-med";
    }

    return "progress-high";
}

/**
 * Extends existing query params with new ones
 */
export function updateQueryParam(params) {
    const query = qs.parse(window.location.search);
    return qs.stringify({
        ...query,
        ...params,
    });
}

/**
 * Returns positions a user has saved but not applied to
 */
export function getSavedPositions() {
    const saved = cookie.get("_ws-save");
    return saved ? saved.split(",").map(Number) : [];
}

/**
 * Add a new position to the saved list
 */
export function addSavedPosition(position) {
    let saved = getSavedPositions();
    saved.push(position.id);
    saved = _uniq(saved);
    cookie.set("_ws-save", saved.join(","));

    return saved;
}

/**
 * Remove a position from the saved list
 */
export function removeSavedPosition(positionID) {
    const saved = _uniq(getSavedPositions().filter((pid) => pid !== positionID));
    cookie.set("_ws-save", saved.join(","));

    return saved;
}

const COMMUTE_COOKIE = "commute";

export function getCommuteData() {
    const data = cookie.get(COMMUTE_COOKIE);

    if (!data) {
        return {};
    }

    const [geohash, address] = data.split("|");
    return {
        geohash,
        address,
    };
}

export function saveCommuteData({ address, geohash }) {
    return cookie.set(COMMUTE_COOKIE, `${geohash}|${address}`, { expires: 7 });
}

export function calculateDurationString(startDate, endDate) {
    const start = moment(startDate, "MM/YYYY");
    const end = endDate === "current" ? moment() : moment(endDate, "MM/YYYY");

    const duration = end.diff(start, "months") + 1;

    const years = Math.floor(duration / 12);
    const months = duration % 12;
    const yearText = years > 1 ? "years" : "year";
    const monthText = months > 1 ? "months" : "month";

    if (years > 0 && months > 0) {
        return `${years} ${yearText} ${months} ${monthText}`;
    }

    if (years > 0) {
        return `${years} ${yearText}`;
    }

    return `${months} ${monthText}`;
}

/**
 * Returns whether a job should be considered a contract job
 */
export function isLikelyContractJob(job) {
    if (Number(job.reason_for_leaving) === REASON_FOR_LEAVING.CONTRACT_ENDED) {
        return true;
    }

    if (Number(job.reason_for_leaving) === REASON_FOR_LEAVING.OTHER) {
        if (job.reason_explain) {
            const reasonExplainLowerArray = job.reason_explain
                .toLowerCase()
                .replace(/[\W_]+/g, " ")
                .split(" ");

            if (
                reasonExplainLowerArray.includes("contract") ||
                reasonExplainLowerArray.includes("seasonal")
            ) {
                return true;
            }
        }
    }

    return false;
}

/* eslint-disable no-useless-escape */
// credit to https://stackoverflow.com/a/46181
export function validateEmail(email) {
    const re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}
/* eslint-enable */

export function validatePhone(val) {
    let phone = "";
    if (val) {
        phone = val.replace(/[\D]/g, "");
    }

    return phone.length === 10;
}

/**
 * Looks through all the inputs in a form and figures out if there are
 * any errors and then puts all the data in a json-able object
 */
export function processFormInputs(form) {
    const data = {};
    const errors = {};

    const inputs = form.querySelectorAll("input, select, textarea");
    const radios = {};

    for (let i = 0; i < inputs.length; i += 1) {
        const field = inputs[i];
        if (!field.name || field.dataset.ignore) {
            continue;
        }

        // Collect radio inputs to be validated at the end
        if (field.type === "radio") {
            if (!(field.name in radios)) {
                radios[field.name] = {
                    value: null,
                    required: false,
                };
            }

            if (field.required) {
                radios[field.name].required = true;
            }

            if (field.checked) {
                radios[field.name].value = field.value;
            }

            continue;
        }

        let { value } = field;

        if (field.required && (!value || value.trim().length === 0)) {
            errors[field.name] = "This field is required";
            continue;
        }

        if (field.dataset.error) {
            errors[field.name] = "This field is required";
            continue;
        }

        if (field.dataset.minlength && value.length < field.dataset.minlength) {
            errors[field.name] = `Please enter at least ${field.dataset.minlength} characters`;
            continue;
        }

        if (field.type === "email" && !validateEmail(value)) {
            errors[field.name] = "Please enter a valid email address";
        }

        // Normally checkboxes don't pass values when unchecked.
        // WELL HOW ABOUT THIS, TIM BERNERS-LEE???
        if (field.type === "checkbox" && !field.checked) {
            value = "0";
        } else if (field.dataset.multiple) {
            value = value.split(",");
        }

        if (!(value || field.dataset.allowEmpty)) {
            continue;
        }

        if (field.name in data && !Array.isArray(data[field.name])) {
            data[field.name] = [data[field.name]];
        }

        if (Array.isArray(data[field.name])) {
            data[field.name].push(value);
        } else {
            data[field.name] = value;
        }
    }

    // Validate radio fields
    Object.entries(radios).forEach(([name, { required, value }]) => {
        if (required && value === null) {
            errors[name] = "This field is required";
        } else if (value) {
            data[name] = value;
        }
    });

    return {
        data,
        errors,
    };
}

export function findClosest(el, selector) {
    let test = el;
    do {
        if (test.matches && test.matches(selector)) {
            return test;
        }

        test = test.parentElement;
    } while (test);

    return null;
}

/* eslint-disable */
/*
 * memoize.js
 * by @philogb and @addyosmani
 * with further optimizations by @mathias
 * and @DmitryBaranovsk
 * perf tests: http://bit.ly/q3zpG3
 * Released under an MIT license.
 */
// Source: https://addyosmani.com/blog/faster-javascript-memoization/
export function memoize(fn) {
    if (!fn.memoize) {
        fn.memoize = {};
    }
    return (...args) => {
        let hash = "";
        let i = 0;

        while (i < args.length) {
            const currentArg = args[i];
            hash += currentArg === Object(currentArg) ? JSON.stringify(currentArg) : currentArg;
            i += 1;
        }

        return hash in fn.memoize ? fn.memoize[hash] : (fn.memoize[hash] = fn.apply(this, args));
    };
}
/* eslint-enable */

export function redirect(url, delayMS = 0) {
    setTimeout(() => {
        window.location.href = url || window.location.href;
    }, delayMS);
}

/**
 * Check if an enter key has been used in an input
 * @param keyCode
 * @returns {boolean}
 */
export function isEnterKey(keyCode) {
    return keyCode === KEY_CODE.ENTER || keyCode === KEY_CODE.RETURN;
}

/**
 * Whether we're on a mobile device!
 * @returns {boolean}
 */
export function isMobile() {
    return window.innerWidth <= MOBILE_BREAK;
}

export function initAddressAutocomplete(autocompleteInput, addressFields, onChange, callback) {
    let autocomplete;
    window.initAutocomplete(autocompleteInput, {
        callback: (ac) => {
            autocomplete = ac;
            if (callback) {
                callback(ac);
            }
        },
        onChange: () => {
            const place = autocomplete.getPlace();
            if (!place) {
                return;
            }
            const addressFieldValues = addressFields.map((f) => [f, ""]);
            /* eslint-disable no-param-reassign */
            place.address_components.forEach((addressComponent) => {
                const addressType = addressComponent.types[0];
                addressFieldValues.forEach((addressField) => {
                    const addressComponents = addressField[0].dataset.addressComponents || "";
                    if (addressComponents.indexOf(addressType) > -1) {
                        if (addressField[1].length !== 0) {
                            addressField[1] += " ";
                        }
                        if (addressType === "locality") {
                            addressField[1] += addressComponent.long_name;
                        } else {
                            addressField[1] += addressComponent.short_name;
                        }
                    }
                });
            });
            addressFieldValues.forEach(([input, value]) => {
                input.value = value;
            });
            /* eslint-enable no-param-reassign */
            if (onChange) {
                onChange();
            }
        },
    });
}

/* eslint-disable no-param-reassign */
/**
 * Replaces one key on an object with another one. Typically used to converted
 * keys that were shortened for data transmission purposes into their full names.
 *
 * @param object - Object to do the replace one
 * @param keyMap - Result of Object.entries() on a dict of {oldKey: newKey}
 */
export function replaceKeys(object, keyMap) {
    keyMap.forEach(([oldKey, newKey]) => {
        if (object[oldKey] !== undefined) {
            if (!object[newKey]) {
                object[newKey] = object[oldKey];
            }
            object[oldKey] = undefined;
        }
    });
}

function copyKeys(object, copyMap) {
    copyMap.forEach(([newKey, oldKey]) => {
        object[newKey] = object[oldKey];
    });
}

/**
 * Transform an individual item
 */
export function transformItem(item, keyMap, copyMap = null) {
    const x = { ...item };
    replaceKeys(x, keyMap);
    if (copyMap) {
        copyKeys(x, copyMap);
    }
    return x;
}

/**
 * Given a list of objects, it will transform each and return the result.
 */
export function transformList(list, keyMap, copyMap = null) {
    list.forEach((item, idx) => {
        const x = { ...item };
        replaceKeys(x, keyMap);
        if (copyMap) {
            copyKeys(x, copyMap);
        }
        list[idx] = x;
    });
    return list;
}
/* eslint-enable */

/**
 * Finds a value in an object using a dotted path string.
 *
 * const o = {'one': 1, 'two': {'three': 3}}
 * getPath(o, 'one');
 * => 1
 * getPath(o, 'two.three')
 * => 3
 * @param object
 * @param path
 * @returns {*}
 */
export function getPath(object, path) {
    const parts = path.split(".");
    if (parts.length === 1) {
        return object[path];
    }

    return parts.reduce((m, p) => m && m[p], object);
}

/**
 * Runs a set of filters against a list.
 */
export function runFilters(list, filters) {
    let filtered = list.slice();

    filters.forEach(({ key = null, value, operator, extraData = {}, func = null }) => {
        /**
         * eq: attrib.key === value
         * in: attrib.key in value (value is a list)
         * in-exists: !(key in attrib) || attrib.key in value
         * exists: !!attrib.key
         * between: attrib.key >= value[0] && attrib.key <= value[1]
         * between_ex: attrib.key >= value[0] && attrib.key < value[1]
         * intersection: attrib.key in value (both are lists)
         * gt: attrib.key > value
         * gte: attrib.key >= value
         * lt: attrib.key < value
         * lte: attrib.key <= value
         */
        if (operator === "eq") {
            filtered = filtered.filter((x) => x[key] === value);
        } else if (operator === "not-eq") {
            filtered = filtered.filter((x) => x[key] !== value);
        } else if (operator === "in") {
            filtered = filtered.filter((x) => value.includes(x[key]));
        } else if (operator === "not-in") {
            filtered = filtered.filter((x) => !value.includes(x[key]));
        } else if (operator === "in-exists") {
            filtered = filtered.filter((x) => !(key in x) || value.includes(x[key]));
        } else if (operator === "exists") {
            filtered = filtered.filter((x) => !isNullOrUndefined(x[key]));
        } else if (operator === "not-exists") {
            filtered = filtered.filter((x) => isNullOrUndefined(x[key]));
        } else if (operator === "between") {
            const [start, end] = value;
            filtered = filtered.filter((x) => x[key] >= start && x[key] <= end);
        } else if (operator === "between_ex") {
            const [start, end] = value;
            filtered = filtered.filter((x) => x[key] >= start && x[key] < end);
        } else if (operator === "func") {
            filtered = func(filtered, value, extraData);
        } else if (operator === "intersection") {
            filtered = filtered.filter((x) => key in x && _intersection(value, x[key]).length);
        } else if (operator === "gt") {
            filtered = filtered.filter((x) => x[key] > value);
        } else if (operator === "lt") {
            filtered = filtered.filter((x) => x[key] < value);
        } else if (operator === "gte") {
            filtered = filtered.filter((x) => x[key] >= value);
        } else if (operator === "lte") {
            filtered = filtered.filter((x) => x[key] <= value);
        }
    });

    return filtered;
}

export const min = (arr, key = null) =>
    arr.reduce((m, v) => {
        const val = key ? v[key] : v;
        if (val === null || val === undefined) return m;
        return m && m < val ? m : val;
    }, null);

export const cartesian = (...arrs) => {
    const nonEmpty = arrs.filter((arr) => arr.length > 0);
    if (nonEmpty.length === 0) {
        return [];
    }
    return nonEmpty.reduce((a, b) => a.flatMap((d) => b.map((e) => _flatten([d, e]))));
};

// simplify anchor onClick handlers with a wrapper to prevent default
// e.g. <a href="#" onClick={preventDefault(yourHandler)}>click me</a>
export const preventDefault =
    (callback, targetOnly = false) =>
    (event) => {
        if (!targetOnly || event.target === event.currentTarget) {
            event.preventDefault();
        }
        callback(event);
    };

// simplify button onClick handlers with a wrapper to blur
// e.g. <button onClick={blur(yourHandler)}>click me</button>
export const blur = (callback) => (event) => {
    event.currentTarget.blur();
    if (callback) {
        callback(event);
    }
};

// Modified via https://stackoverflow.com/a/5624139/203700
export function hexToRgb(hex) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const expanded = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expanded);
    if (!result) {
        return null;
    }

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
}

const stripTrailingSlash = (str) => (str.endsWith("/") ? str.slice(0, -1) : str);

export function urlMatches(url1, url2 = window.location.pathname, exactMatch = true) {
    const strippedURL1 = stripTrailingSlash(url1);
    const strippedURL2 = stripTrailingSlash(url2);

    return exactMatch ? strippedURL1 === strippedURL2 : strippedURL2.includes(strippedURL1);
}

export const copyTextToClipboard = async (text) => {
    if ("clipboard" in navigator) {
        await navigator.clipboard.writeText(text);
    } else if ("clipboardData" in window) {
        window.clipboardData.setData("Text", text);
    } else {
        document.execCommand("copy", true, text);
    }
};

export const trimLength = (str, maxLength) => {
    if (str.length <= maxLength) {
        return str;
    }
    const trimmedLength = maxLength - 3;
    return `${str.substring(0, trimmedLength)}...`;
};

export const formatISODate = (date, assumeUtc = false) => {
    let utcDate = date;
    // a naive date should be assumed as UTC.
    if (assumeUtc && !date.endsWith("Z") && !date.includes("+")) {
        utcDate = `${date}Z`;
    }
    return new Date(utcDate).toISOString().slice(0, 10);
};

export const formatGMTTime = (date) => {
    const hours = new Date(date).getUTCHours() % 12 || 12;
    const ampm = new Date(date).getUTCHours() >= 12 ? "pm" : "am";
    const minutes = new Date(date).getUTCMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes} ${ampm}`;
};

// Given html text and an object of tag names and replacement values replace all variations
// example replaceTags = { br: " " }
const replaceHTMLTags = (html, replaceTags = {}) =>
    Object.keys(replaceTags).reduce((acc, tag) => {
        const replacement = replaceTags[tag];
        // If tag is "br" this will match <br>, </br>, <br />, <br >
        const re = new RegExp(`</?${tag} ?/?>`, "g");
        return acc.replace(re, replacement);
    }, html);

// Sanitize all html tags/attributes and optionally replace some tags with a specific text value
// NOTE: replacing tags is not recommended unless strictly necessary
export const saniziteHTMLCompletely = (html, replaceTags = {}) => {
    const tags = _isEmpty(replaceTags) ? [] : Object.keys(replaceTags);
    const sanitized = sanitizeHtml(html, { allowedTags: tags, allowedAttributes: {} });
    return replaceHTMLTags(sanitized, replaceTags).trim();
};
